import InternalServerError from '@/views/error/InternalServerError';
import { useLayoutEffect } from 'react';
import { useRouteError } from 'react-router-dom';

// Session storage key to store the last reload timestamp - prevents infinite reload loops
const RELOAD_AT_KEY = 'chunkReloadAt';
// Throttle time (ms) - suppress reload if error reoccurs within this window
const RELOAD_THROTTLE = 10_000;

// Detects if an error is a ChunkLoadError caused by cached old chunk hashes
// after a new deployment changes the content hashes of code-split chunks.
function isChunkLoadError(error: unknown): boolean {
  const name = (error as { name?: string } | null)?.name;
  const message = (error as { message?: string } | null)?.message ?? '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);

  // Use useLayoutEffect to reload before paint to avoid error screen flickering.
  // If recently reloaded (within throttle), skip to prevent infinite loops.
  useLayoutEffect(() => {
    if (!chunkError) return;
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? '0');
    if (Date.now() - lastReloadAt > RELOAD_THROTTLE) {
      sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
      window.location.reload();
    }
  }, [chunkError]);

  // Chunk errors are recoverable by reload. Other errors or throttle exceeded: show error page.
  return <InternalServerError />;
}
